import api from '@/store/api/donor'
import { downloadBlob } from '@/helpers/downloadBlob'
import { resolveTaxPackForPeriod } from '@/helpers/resolveTaxPackForPeriod'
import { taxPackFilename } from '@/helpers/taxPackFilename'
import {
  downloadTaxPackPdfFile,
  generateTaxPackPdfBlob,
} from '@/services/taxPackPdf/buildTaxPackPdf'
import type { DonorInformation } from '@/types'
import type {
  DonorDonation,
  DonorFinancialYear,
} from '@/types/donorFinancialYear'
import type { TaxPackExportOptions } from '@/types/taxPack'
import type { UserStatement } from '@/types/userStatement'

export type TaxPackDownloadSources = {
  statements: UserStatement[]
  financialYears: DonorFinancialYear[]
  manualDonations: DonorDonation[]
  /** Used when the period has no issued statement (e.g. £0 gap years). */
  fallbackDonor?: DonorInformation | null
}

/** True when the browser should build the PDF locally (Mirage binary responses are unreliable). */
function shouldGenerateClientPdf(): boolean {
  return true // import.meta.env.MODE === 'mockapi' || import.meta.env.DEV
}

async function readApiErrorFromBlob(blob: Blob): Promise<string | null> {
  try {
    const text = await blob.text()
    const parsed = JSON.parse(text) as { detail?: string; title?: string }
    return parsed.detail ?? parsed.title ?? null
  } catch {
    return null
  }
}

async function fetchTaxPackPdfFromApi(
  period: string,
  options: TaxPackExportOptions
): Promise<Blob> {
  const response = await api.fetchTaxPackPdf(period, options)
  const blob = response.data as Blob

  if (!blob || blob.size === 0) {
    throw new Error('Statement PDF API returned an empty file')
  }

  const type = blob.type?.toLowerCase() ?? ''
  if (type.includes('json') || type.includes('problem')) {
    const message = await readApiErrorFromBlob(blob)
    throw new Error(message ?? 'Statement PDF API returned an error')
  }

  return blob
}

/**
 * Downloads a statement PDF for a tax year period.
 * Dev/mock builds generate the PDF in-browser; production tries the API first.
 * Periods without an issued statement still get a client-side PDF (e.g. £0 years).
 */
export async function downloadTaxPackPdf(
  period: string,
  options: TaxPackExportOptions,
  sources: TaxPackDownloadSources
): Promise<void> {
  const pack = resolveTaxPackForPeriod(
    period,
    sources.financialYears,
    sources.statements,
    sources.manualDonations,
    sources.fallbackDonor
  )

  if (!pack) {
    throw new Error(`Unable to build statement PDF for period ${period}`)
  }

  const filename = taxPackFilename(period)
  const hasIssuedStatement = sources.statements.some((row) => row.period === period)

  // Only call the API when an issued statement exists; gap/£0 placeholder years
  // are generated client-side.
  if (!shouldGenerateClientPdf() && hasIssuedStatement) {
    try {
      const blob = await fetchTaxPackPdfFromApi(period, options)
      downloadBlob(blob, filename)
      return
    } catch (error) {
      console.warn(
        'Statement PDF API unavailable; generating client-side PDF',
        error
      )
    }
  }

  if (typeof window !== 'undefined') {
    await downloadTaxPackPdfFile(pack, options, filename)
    return
  }

  const blob = await generateTaxPackPdfBlob(pack, options)
  downloadBlob(blob, filename)
}
